import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../../awsAuth";

import FeedActions from "../Feed/FeedActions";
import ActionBar from "../common/ActionBar";
import ShareModal from "../Shared/ShareModal";
import CommentsModal from "../Comments/CommentsModal";
import SaveSheetModal from "../Saved/SaveSheetModal";
import PostCard from "../Feed/Post/PostCard";

import {
  UI_TTL,
  HARD_TTL,
  cacheGetWithAge,
  cacheSet,
} from "../../utils/swrcache";

import clipIcon from "./img/clip.png";
import publishIcon from "./img/publish.png";
import "./CommunityFeed.scss";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.amazonaws.com/DefaultAvatar/avatar.png";

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const COMMUNITY_FEED_PAGE_LIMIT = 20;

const getAuthConfig = async () => {
  const token = await getAccessToken();
  if (!token) return null;

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/* time-ago helper */
const formatTimeAgo = (ts) => {
  if (!ts) return "";
  const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 1) return "щойно";
  if (diffMin < 60) return `${diffMin} хв тому`;
  if (diffH < 24) return `${diffH} год тому`;
  if (diffD === 1) return `вчора`;

  const months = [
    "січня",
    "лютого",
    "березня",
    "квітня",
    "травня",
    "червня",
    "липня",
    "серпня",
    "вересня",
    "жовтня",
    "листопада",
    "грудня",
  ];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

/* comments counter */
async function fetchCommentCount(listingId) {
  try {
    if (!listingId) return 0;

    const tries = [
      `${API}/api/comments/stats/${encodeURIComponent(listingId)}?type=post`,
      `${API}/api/comments/stats/${encodeURIComponent(listingId)}`,
      `${API}/api/comments/stats/post/${encodeURIComponent(listingId)}`,
      `${API}/api/comments/stats?postId=${encodeURIComponent(listingId)}`,
    ];

    for (const url of tries) {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) continue;
        const d = await r.json();
        const v =
          Number(
            d?.count ??
              d?.total ??
              d?.commentsCount ??
              d?.data?.count ??
              d?.data?.total ??
              0
          ) || 0;
        return Math.max(0, v);
      } catch {
        // continue
      }
    }

    return 0;
  } catch {
    return 0;
  }
}

/* shares counter */
async function fetchShareCount(postId) {
  try {
    if (!postId) return 0;

    const tries = [
      `${API}/api/shares/stats/${encodeURIComponent(postId)}?type=post`,
      `${API}/api/shares/stats/${encodeURIComponent(postId)}`,
      `${API}/api/shares/${encodeURIComponent(postId)}`,
      `${API}/api/shares/count/${encodeURIComponent(postId)}`,
      `${API}/api/posts/share-stats/${encodeURIComponent(postId)}`,
    ];

    for (const url of tries) {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) continue;
        const d = await r.json();
        const v =
          Number(
            d?.count ??
              d?.total ??
              d?.sharesCount ??
              d?.data?.count ??
              d?.data?.total ??
              0
          ) || 0;
        return Math.max(0, v);
      } catch {
        // continue
      }
    }

    return 0;
  } catch {
    return 0;
  }
}

const CommunityFeed = ({
  communityId,
  userId,
  canSeeContent,
  canPost,
  commentsEnabled,
  isBlockedByCommunity,
  onOpenPhotoViewer,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const USE_POSTCARD = true;

  /* current user (composer) */
  const [meAvatar, setMeAvatar] = useState(DEFAULT_AVATAR);
  const [meName, setMeName] = useState("");

  /* posts state */
  const [posts, setPosts] = useState([]);
  const [likedIds, setLikedIds] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [likesCount, setLikesCount] = useState({});
  const [feedLoading, setFeedLoading] = useState(true);

  // hares counter state (після refresh саме воно падало в 0)
  const [shareCounts, setShareCounts] = useState({});

  /* expanded / collapsed text per post */
  const [expandedPosts, setExpandedPosts] = useState({});

  /* comments state */
  const [commentCounts, setCommentCounts] = useState({});
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsListingId, setCommentsListingId] = useState(null);
  const [commentsListingAuthorId, setCommentsListingAuthorId] =
    useState(null);

  /* publish progress state */
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);

  /* post actions */
  const [postActionsOpen, setPostActionsOpen] = useState(false);
  const [postActionsItem, setPostActionsItem] = useState(null);

  /* save/share */
  const [saveSheetOpen, setSaveSheetOpen] = useState(false);
  const [saveSheetPostId, setSaveSheetPostId] = useState(null);
  const [saveSheetThumb, setSaveSheetThumb] = useState(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareText, setShareText] = useState("");
  const [sharePostId, setSharePostId] = useState(null);

  const clickTimerRef = useRef(null);

  /* composer refs */
  const attachInputRef = useRef(null);

  /* deep-link target поста в межах спільноти */
  const [highlightPostId, setHighlightPostId] = useState(null);

  /* pagination state */
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const id =
      params.get("post") ||
      params.get("highlight") ||
      params.get("highlightPost");
    setHighlightPostId(id);
  }, [location.search]);

  useEffect(() => {
    if (!highlightPostId) return;

    const targetId = highlightPostId;
    const escaped =
      typeof CSS !== "undefined" && CSS.escape
        ? CSS.escape(targetId)
        : targetId;
    const selector = `[data-post-id="${escaped}"]`;

    const tryScroll = () => {
      const el = document.querySelector(selector);
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("pulse-highlight");
      setTimeout(() => el.classList.remove("pulse-highlight"), 1800);
      return true;
    };

    if (tryScroll()) return;
    const t1 = setTimeout(tryScroll, 300);
    const t2 = setTimeout(tryScroll, 800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [highlightPostId, posts.length]);

  /* me (composer) */
  useEffect(() => {
    let cancelled = false;
    const fetchMe = async () => {
      if (!userId) return;
      try {
        const res = await fetch(`${API}/api/account/${userId}`, {
          cache: "no-store",
        });
        if (!res.ok) {
          return;
        }
        const u = await res.json();
        if (cancelled) return;
        setMeAvatar(u?.avatar || DEFAULT_AVATAR);
        setMeName(u?.username || "");
      } catch {}
    };
    fetchMe();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const parsePaginatedResponse = useCallback((data) => {
    
    if (Array.isArray(data)) {
      return { items: data, nextCursor: null, hasMore: false };
    }

    if (data && typeof data === "object") {
      const inner =
        data?.items || data?.posts || data?.data?.items || data?.data?.posts;

      const items = Array.isArray(inner)
        ? inner
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.posts)
        ? data.posts
        : Array.isArray(data?.data)
        ? data.data
        : [];

      const nextCursor =
        data?.nextCursor ??
        data?.cursor ??
        data?.data?.nextCursor ??
        data?.data?.cursor ??
        null;

      const hasMore =
        typeof data?.hasMore === "boolean"
          ? data.hasMore
          : typeof data?.data?.hasMore === "boolean"
          ? data.data.hasMore
          : !!nextCursor;

      return { items, nextCursor, hasMore };
    }

    return { items: [], nextCursor: null, hasMore: false };
  }, []);

  const appendDedupe = useCallback((prev, next) => {
    const seen = new Set((prev || []).map((p) => String(p?.postId || p?.id || "")));
    const out = [...(prev || [])];
    for (const p of next || []) {
      const id = String(p?.postId || p?.id || "");
      if (!id) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(p);
    }
    return out;
  }, []);

  /* feed */
  useEffect(() => {
    if (!communityId) return;

    setCursor(null);
    setHasMore(false);
    setLoadingMore(false);

    if (!canSeeContent) {
      setPosts([]);
      setFeedLoading(false);
      return;
    }

    let cancelled = false;
    setFeedLoading(true);

    const CK_POSTS = `community:posts:${communityId}`;
    const cached = cacheGetWithAge(CK_POSTS, HARD_TTL);
    if (cached && !cancelled) {
      setPosts(Array.isArray(cached.data) ? cached.data : []);
      setFeedLoading(false);
    }

    const fetchFresh = async () => {
      try {
        const baseParams = new URLSearchParams();
        baseParams.set("paginated", "1");
        baseParams.set("limit", String(COMMUNITY_FEED_PAGE_LIMIT));
        if (userId) baseParams.set("viewerId", userId);

        const url = `${API}/api/posts/community/${communityId}?${baseParams.toString()}`;

        const authConfig = await getAuthConfig();
        if (!authConfig) {
          setPosts([]);
          setFeedLoading(false);
          return;
        }

        const res = await fetch(url, {
          cache: "no-store",
          headers: {
            ...(authConfig.headers || {}),
          },
        });

        if (!res.ok) {
          if (res.status === 403) {
            setPosts([]);
            setFeedLoading(false);
            return;
          }

          if (res.status === 404) {
            console.warn("community posts 404 for", communityId);
            setPosts([]);
            cacheSet(CK_POSTS, []);
            setFeedLoading(false);
            return;
          }

          throw new Error("HTTP " + res.status);
        }

        const data = await res.json();
        if (cancelled) return;

        const { items: rawItems, nextCursor, hasMore: more } =
          parsePaginatedResponse(data);

        const normalized = (Array.isArray(rawItems) ? rawItems : []).map((p) => ({
          ...p,
          postId: p.postId || p.id,
          createdAt: p.createdAt || Date.now(),
        }));

        normalized.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        setPosts(normalized);
        cacheSet(CK_POSTS, normalized);

        setCursor(nextCursor || null);
        setHasMore(!!(more && nextCursor));

        if (userId) {
          const likesMap = {};
          const likedByUser = [];
          const savedByUser = [];

          await Promise.all(
            normalized.map(async (item) => {
              const pid = item.postId;
              try {
                const authConfig = await getAuthConfig();
                if (!authConfig) return;

                const likesRes = await axios.get(
                  `${API}/api/likes/${pid}`,
                  authConfig
                );
                likesMap[pid] = likesRes.data?.count ?? 0;
                if (
                  Array.isArray(likesRes.data?.users) &&
                  likesRes.data.users.includes(userId)
                ) {
                  likedByUser.push(pid);
                }
              } catch {}
            })
          );

          try {
            const authConfig = await getAuthConfig();
            if (!authConfig) return;

            const savedRes = await axios.get(
              `${API}/api/saved/${userId}`,
              authConfig
            );
            const sIds = Array.isArray(savedRes.data)
              ? savedRes.data.map((s) => s.postId)
              : [];
            savedByUser.push(...sIds);
          } catch {}

          setLikesCount(likesMap);
          setLikedIds(likedByUser);
          setSavedIds(savedByUser);
        }

        setFeedLoading(false);

        const ids = normalized.map((p) => p.postId).filter(Boolean);
        if (ids.length) {
          const cMap = {};
          const sMap = {};

          await Promise.all(
            ids.map(async (pid) => {
              const seededComments = normalized.find((x) => x.postId === pid)
                ?.commentsCount;
              cMap[pid] =
                typeof seededComments === "number"
                  ? seededComments
                  : await fetchCommentCount(pid);

              const seededShares = normalized.find((x) => x.postId === pid)
                ?.sharesCount;
              sMap[pid] =
                typeof seededShares === "number"
                  ? seededShares
                  : await fetchShareCount(pid);
            })
          );

          if (!cancelled) {
            setCommentCounts(cMap);
            setShareCounts(sMap);
          }
        } else {
          setCommentCounts({});
          setShareCounts({});
        }
      } catch (e) {
        console.error("❌ community posts:", e);
        setFeedLoading(false);
      }
    };

    if (!cached) fetchFresh();
    else if (cached.ageMs <= UI_TTL) {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(fetchFresh, { timeout: 2000 });
      } else {
        setTimeout(fetchFresh, 0);
      }
    } else {
      fetchFresh();
    }

    return () => {
      cancelled = true;
    };
  }, [communityId, userId, canSeeContent, parsePaginatedResponse]);

  const loadMore = useCallback(async () => {
    if (!communityId) return;
    if (!canSeeContent) return;
    if (loadingMore) return;
    if (!hasMore || !cursor) return;

    try {
      setLoadingMore(true);

      const params = new URLSearchParams();
      params.set("paginated", "1");
      params.set("limit", String(COMMUNITY_FEED_PAGE_LIMIT));
      params.set("cursor", String(cursor));
      if (userId) params.set("viewerId", userId);

      const url = `${API}/api/posts/community/${communityId}?${params.toString()}`;

      const authConfig = await getAuthConfig();
      if (!authConfig) {
        setHasMore(false);
        setCursor(null);
        setLoadingMore(false);
        return;
      }

      const res = await fetch(url, {
        cache: "no-store",
        headers: {
          ...(authConfig.headers || {}),
        },
      });
      if (!res.ok) {
        setHasMore(false);
        setCursor(null);
        return;
      }

      const data = await res.json();
      const { items: rawItems, nextCursor, hasMore: more } =
        parsePaginatedResponse(data);

      const normalized = (Array.isArray(rawItems) ? rawItems : []).map((p) => ({
        ...p,
        postId: p.postId || p.id,
        createdAt: p.createdAt || Date.now(),
      }));

      normalized.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      const newIds = normalized.map((p) => p.postId).filter(Boolean);

      setPosts((prev) => {
        const next = appendDedupe(prev, normalized);
        try {
          const CK_POSTS = `community:posts:${communityId}`;
          cacheSet(CK_POSTS, next);
        } catch {}
        return next;
      });

      setCursor(nextCursor || null);
      setHasMore(!!(more && nextCursor));

      if (userId && newIds.length) {
        const likesMapPatch = {};
        const likedPatch = [];

        await Promise.all(
          newIds.map(async (pid) => {
            try {
              const authConfig = await getAuthConfig();
              if (!authConfig) return;

              const likesRes = await axios.get(
                `${API}/api/likes/${pid}`,
                authConfig
              );
              likesMapPatch[pid] = likesRes.data?.count ?? 0;
              if (
                Array.isArray(likesRes.data?.users) &&
                likesRes.data.users.includes(userId)
              ) {
                likedPatch.push(pid);
              }
            } catch {}
          })
        );

        setLikesCount((prev) => ({ ...prev, ...likesMapPatch }));
        if (likedPatch.length) {
          setLikedIds((prev) => {
            const s = new Set(prev || []);
            for (const id of likedPatch) s.add(id);
            return Array.from(s);
          });
        }

      }

      if (newIds.length) {
        const cPatch = {};
        const sPatch = {};

        await Promise.all(
          newIds.map(async (pid) => {
            const seededComments = normalized.find((x) => x.postId === pid)
              ?.commentsCount;
            cPatch[pid] =
              typeof seededComments === "number"
                ? seededComments
                : await fetchCommentCount(pid);

            const seededShares = normalized.find((x) => x.postId === pid)
              ?.sharesCount;
            sPatch[pid] =
              typeof seededShares === "number"
                ? seededShares
                : await fetchShareCount(pid);
          })
        );

        setCommentCounts((prev) => ({ ...prev, ...cPatch }));
        setShareCounts((prev) => ({ ...prev, ...sPatch }));
      }
    } catch (e) {
      console.error("❌ community loadMore:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [
    communityId,
    userId,
    canSeeContent,
    loadingMore,
    hasMore,
    cursor,
    parsePaginatedResponse,
    appendDedupe,
  ]);

  /* sentinel observer */
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    if (!hasMore) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadMore();
        });
      },
      {
        root: null,
        rootMargin: "140px 0px 300px 0px",
        threshold: 0,
      }
    );

    obs.observe(node);
    return () => {
      try {
        obs.unobserve(node);
      } catch {}
      obs.disconnect();
    };
  }, [hasMore, loadMore]);

  useEffect(() => {
    if (!isPublishing) return;
    const interval = setInterval(() => {
      setPublishProgress((prev) => {
        if (prev >= 90) return prev;
        const step = 3 + Math.random() * 4;
        return Math.min(prev + step, 90);
      });
    }, 200);

    return () => clearInterval(interval);
  }, [isPublishing]);

  /* composer */
  const [text, setText] = useState("");
  const [fileBlob, setFileBlob] = useState(null);
  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const onPickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setFileBlob(f);
    setFileName(f.name);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const clearFile = () => {
    setFileBlob(null);
    setFileName("");
    setPreviewUrl("");
    if (attachInputRef.current) attachInputRef.current.value = "";
  };

  const onPublish = async () => {
  if (!userId) return;
  if (!text && !fileBlob) return;
  if (isPublishing) return;

  const CK_POSTS = communityId
    ? `community:posts:${communityId}`
    : null;

  setIsPublishing(true);
  setPublishProgress((prev) => (prev < 10 ? 10 : prev));

  try {
    const authConfig = await getAuthConfig();
    if (!authConfig) {
      alert("Сесія недійсна. Увійдіть повторно.");
      setPublishProgress(0);
      return;
    }

    let photoUrl = "";
    if (fileBlob) {
      const form = new FormData();
      form.append("file", fileBlob);

      const up = await axios.post(
        `${API}/api/communities/upload-avatar`,
        form,
        authConfig
      );

      photoUrl = up.data?.url || "";
    }

    const res = await axios.post(
      `${API}/api/posts`,
      {
        userId,
        text: text?.trim() || "",
        photo: photoUrl,
        communityId,
      },
      authConfig
    );

      let serverPost =
        (res.data && res.data.post) ||
        res.data?.createdPost ||
        res.data?.item ||
        res.data ||
        {};

      if (!serverPost.postId && !serverPost.id) {
        if (res.data && typeof res.data === "object") {
          const maybeInner =
            res.data.post ||
            res.data.data ||
            res.data.result ||
            null;
          if (maybeInner && (maybeInner.postId || maybeInner.id)) {
            serverPost = maybeInner;
          }
        }
      }

      let normalized;

      if (serverPost && (serverPost.postId || serverPost.id)) {
        normalized = {
          ...serverPost,
          postId: serverPost.postId || serverPost.id,
          authorName: serverPost.authorName || meName,
          authorAvatar: serverPost.authorAvatar || meAvatar,
          communityId: serverPost.communityId || communityId,
        };
      } else {
        const fallbackId =
          res.data?.id ||
          res.data?.postId ||
          `local_${Date.now()}_${Math.random()
            .toString(36)
            .slice(2, 8)}`;

        normalized = {
          postId: fallbackId,
          id: fallbackId,
          userId,
          text: text?.trim() || "",
          photo: photoUrl,
          communityId,
          createdAt: Date.now(),
          authorName: meName,
          authorAvatar: meAvatar,
        };
      }

      setPosts((prev) => {
        const next = [normalized, ...prev];
        if (CK_POSTS) {
          try {
            cacheSet(CK_POSTS, next);
          } catch {}
        }
        return next;
      });
      setLikesCount((prev) => ({
        ...prev,
        [normalized.postId]: 0,
      }));
      setCommentCounts((prev) => ({
        ...prev,
        [normalized.postId]: 0,
      }));
      // shares map init, щоб не було undefined
      setShareCounts((prev) => ({
        ...prev,
        [normalized.postId]: 0,
      }));

      setText("");
      clearFile();

      setPublishProgress(95);
    } catch (e) {
      console.error("❌ publish:", e);
      alert("Не вдалося опублікувати пост.");
      setPublishProgress(0);
    } finally {
      setPublishProgress((prev) => (prev < 100 ? 100 : prev));
      setTimeout(() => {
        setIsPublishing(false);
        setPublishProgress(0);
      }, 400);
    }
  };

  /* like/save */
    const toggleLike = async (item) => {
    if (!userId) return;
    const pid = item.postId;
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig) return;

      if (likedIds.includes(pid)) {
        await axios.delete(`${API}/api/likes`, {
          ...authConfig,
          data: { postId: pid, userId },
        });
        setLikedIds((p) => p.filter((x) => x !== pid));
        setLikesCount((p) => ({
          ...p,
          [pid]: Math.max(0, (p[pid] || 1) - 1),
        }));
      } else {
        await axios.post(
          `${API}/api/likes`,
          { postId: pid, userId },
          authConfig
        );
        setLikedIds((p) => [...p, pid]);
        setLikesCount((p) => ({ ...p, [pid]: (p[pid] || 0) + 1 }));
      }
    } catch (err) {
      console.error("❌ like:", err);
    }
  };

    const quickSaveToAllIfNeeded = useCallback(
    async (post) => {
      if (!userId || !post?.postId) return;
      const pid = post.postId;
      if (savedIds.includes(pid)) return;
      try {
        const authConfig = await getAuthConfig();
        if (!authConfig) return;

        setSavedIds((p) => (p.includes(pid) ? p : [...p, pid]));
        await axios.post(
          `${API}/api/saved`,
          {
            userId,
            postId: pid,
            type: "post",
            collectionId: "all",
          },
          authConfig
        );
      } catch (err) {
        console.error("❌ quickSave:", err);
        setSavedIds((p) => p.filter((x) => x !== pid));
      }
    },
    [userId, savedIds]
  );

    const toggleSave = async (item) => {
    if (!userId) return;
    const pid = item.postId;
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig) return;

      if (savedIds.includes(pid)) {
        await axios.delete(`${API}/api/saved`, {
          ...authConfig,
          data: { userId, postId: pid },
        });
        setSavedIds((p) => p.filter((x) => x !== pid));
      } else {
        await axios.post(
          `${API}/api/saved`,
          {
            userId,
            postId: pid,
            type: "post",
          },
          authConfig
        );
        setSavedIds((p) => [...p, pid]);
      }
    } catch (err) {
      console.error("❌ save:", err);
    }
  };

  const handleDoubleClick = useCallback(
    (item) => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      if (!likedIds.includes(item.postId)) toggleLike(item);
    },
    [likedIds]
  );

  /* photo viewer bridge */
  const openPhotoViewer = useCallback(
    (post, startIndex = 0) => {
      const list =
        Array.isArray(post?.photos) && post.photos.length
          ? post.photos.map((p) =>
              typeof p === "string"
                ? { src: p, alt: post?.text || "" }
                : { src: p.url || p.src, alt: p.alt || post?.text || "" }
            )
          : post?.photo
          ? [{ src: post.photo, alt: post?.text || "" }]
          : [];

      if (!list.length) return;

      if (typeof onOpenPhotoViewer === "function") {
        onOpenPhotoViewer(list, startIndex);
        return;
      }

      try {
        window.dispatchEvent(
          new CustomEvent("cv:openPhotoViewer", {
            detail: { photos: list, startIndex },
          })
        );
      } catch {}
    },
    [onOpenPhotoViewer]
  );

  /* comments open/close */
  const openCommentsForPost = useCallback(
    (post) => {
      if (!commentsEnabled) return;
      const pid = post?.postId;
      const authorId =
        post?.userId || post?.authorId || post?.ownerId || null;
      if (!pid) return;
      setCommentsListingId(pid);
      setCommentsListingAuthorId(authorId);
      setCommentsOpen(true);
    },
    [commentsEnabled]
  );

  const closeComments = useCallback(() => setCommentsOpen(false), []);

  const onCommentsCountChange = useCallback(
    (payload) => {
      if (payload && typeof payload === "object") {
        const { listingId, postId, total, delta } = payload;
        const id = listingId || postId || commentsListingId;
        if (!id) return;

        if (typeof total === "number") {
          setCommentCounts((prev) => ({
            ...prev,
            [id]: Math.max(0, total),
          }));
          return;
        }

        if (typeof delta === "number") {
          setCommentCounts((prev) => {
            const current = Number(prev[id] || 0);
            return {
              ...prev,
              [id]: Math.max(0, current + delta),
            };
          });
          return;
        }

        return;
      }

      if (typeof payload === "number") {
        if (!commentsListingId || !payload) return;
        const delta = payload;
        setCommentCounts((prev) => {
          const cur = Number(prev[commentsListingId] || 0);
          return {
            ...prev,
            [commentsListingId]: Math.max(0, cur + delta),
          };
        });
      }
    },
    [commentsListingId]
  );

  /* post actions (⋮) */
  const openPostActions = useCallback((post) => {
    if (!post) return;
    setPostActionsItem({
      postId: post.postId,
      userId: post.userId || post.authorId || post.ownerId || null,
      createdAt: post.createdAt || Date.now(),
      authorName: post.authorName || "Автор",
      text: post.text || "",
    });
    setPostActionsOpen(true);
  }, []);

  const closePostActions = useCallback(() => {
    setPostActionsOpen(false);
    setPostActionsItem(null);
  }, []);

  const handlePostDeleted = useCallback(
    (_type, deletedId) => {
      if (!deletedId) return;
      setPosts((prev) => prev.filter((x) => (x.postId || x.id) !== deletedId));

      setLikesCount((prev) => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });

      setCommentCounts((prev) => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });

      // share map cleanup
      setShareCounts((prev) => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });

      setLikedIds((prev) => prev.filter((x) => x !== deletedId));
      setSavedIds((prev) => prev.filter((x) => x !== deletedId));

      closePostActions();
    },
    [closePostActions]
  );

  const publishDisabled = !userId || (!text && !fileBlob) || isPublishing;

  const buildCommunityPostUrl = useCallback(
    (pid) => {
      if (!communityId || !pid) return "";
      const params = new URLSearchParams();
      if (userId) params.set("userId", userId);
      params.set("post", pid);
      return `${window.location.origin}/communities/${encodeURIComponent(
        communityId
      )}?${params.toString()}`;
    },
    [communityId, userId]
  );

  const handleSyncCounts = useCallback(
    (patch) => {
      const pid = patch?.postId;
      if (!pid) return;

      if (typeof patch.likesCount === "number") {
        setLikesCount((prev) => ({ ...prev, [pid]: Math.max(0, Number(patch.likesCount || 0)) }));
      }
      if (typeof patch.commentsCount === "number") {
        setCommentCounts((prev) => ({ ...prev, [pid]: Math.max(0, Number(patch.commentsCount || 0)) }));
      }
      if (typeof patch.sharesCount === "number") {
        setShareCounts((prev) => ({ ...prev, [pid]: Math.max(0, Number(patch.sharesCount || 0)) }));
      }
    },
    []
  );

  useEffect(() => {
    if (!USE_POSTCARD) return;

    const onLikes = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      if (typeof d.liked === "boolean") {
        setLikedIds((prev) => {
          const s = new Set(prev || []);
          if (d.liked) s.add(pid);
          else s.delete(pid);
          return Array.from(s);
        });
      }

      if (typeof d.absolute === "number") {
        setLikesCount((prev) => ({ ...prev, [pid]: Math.max(0, Number(d.absolute || 0)) }));
      } else if (typeof d.delta === "number") {
        setLikesCount((prev) => {
          const cur = Number(prev?.[pid] || 0);
          return { ...prev, [pid]: Math.max(0, cur + Number(d.delta || 0)) };
        });
      }
    };

    const onSaved = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      if (typeof d.saved === "boolean") {
        setSavedIds((prev) => {
          const s = new Set(prev || []);
          if (d.saved) s.add(pid);
          else s.delete(pid);
          return Array.from(s);
        });
      }
    };

    const onComments = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      if (typeof d.absolute === "number") {
        setCommentCounts((prev) => ({ ...prev, [pid]: Math.max(0, Number(d.absolute || 0)) }));
      } else if (typeof d.delta === "number") {
        setCommentCounts((prev) => {
          const cur = Number(prev?.[pid] || 0);
          return { ...prev, [pid]: Math.max(0, cur + Number(d.delta || 0)) };
        });
      }
    };

    const onShares = (e) => {
      const d = e?.detail || {};
      const pid = d.postId || d.listingId;
      if (!pid) return;

      if (typeof d.absolute === "number") {
        setShareCounts((prev) => ({ ...prev, [pid]: Math.max(0, Number(d.absolute || 0)) }));
      } else if (typeof d.delta === "number") {
        setShareCounts((prev) => {
          const cur = Number(prev?.[pid] || 0);
          return { ...prev, [pid]: Math.max(0, cur + Number(d.delta || 0)) };
        });
      }
    };

    window.addEventListener("cv-likes-updated", onLikes);
    window.addEventListener("cv-saved-updated", onSaved);
    window.addEventListener("cv-comments-updated", onComments);
    window.addEventListener("cv-shares-updated", onShares);

    return () => {
      window.removeEventListener("cv-likes-updated", onLikes);
      window.removeEventListener("cv-saved-updated", onSaved);
      window.removeEventListener("cv-comments-updated", onComments);
      window.removeEventListener("cv-shares-updated", onShares);
    };
  }, [USE_POSTCARD]);

  if (!canSeeContent) return null;

  return (
    <div className="community-feed-wrap">
      {/* composer */}
      {canPost && !isBlockedByCommunity && (
        <div
          className={`community-composer ${
            isPublishing ? "is-disabled" : ""
          }`}
        >
          {isPublishing && (
            <div className="composer-publish-progress">
              <div className="progress-track">
                <div
                  className="progress-bar"
                  style={{ width: `${Math.round(publishProgress)}%` }}
                />
              </div>
              <span className="progress-label">
                Публікуємо… {Math.min(100, Math.round(publishProgress))}%
              </span>
            </div>
          )}

          <div className="composer-row">
            <img
              className="composer-avatar"
              src={meAvatar || DEFAULT_AVATAR}
              alt=""
            />
            <textarea
              placeholder="Напишіть або запитайте щось…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>

          {previewUrl && (
            <div className="composer-preview">
              <img src={previewUrl} alt="preview" />
            </div>
          )}

          <div className="composer-actions">
            <input
              ref={attachInputRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className="circle-glass-btn attach"
              title="Прикріпити фото"
              aria-label="Прикріпити фото"
              onClick={() =>
                attachInputRef.current && attachInputRef.current.click()
              }
              disabled={isPublishing}
            >
              <img src={clipIcon} alt="" />
            </button>

            {fileName && (
              <span className="file-name">
                {fileName}
                <button
                  className="remove-file"
                  onClick={clearFile}
                  title="Видалити файл"
                  disabled={isPublishing}
                >
                  ×
                </button>
              </span>
            )}

            <button
              type="button"
              className={`circle-glass-btn publish ${
                publishDisabled ? "is-disabled" : ""
              }`}
              title={!userId ? "Увійдіть, щоб публікувати" : "Опублікувати"}
              aria-label="Опублікувати"
              onClick={onPublish}
              disabled={publishDisabled}
            >
              <img src={publishIcon} alt="" />
            </button>
          </div>
        </div>
      )}

      {/* feed */}
      <div className="community-feed">
        {feedLoading && <p className="feed__loading">Завантаження…</p>}
        {!feedLoading && posts.length === 0 && (
          <p className="feed__empty">Поки немає публікацій у цій спільноті.</p>
        )}

        {posts.map((p) => {
          const pid = p.postId;
          const isLiked = likedIds.includes(pid);
          const isSaved = savedIds.includes(pid);
          const timeAgo = formatTimeAgo(p.createdAt);

          const commentsNum = Number(
            commentCounts && typeof commentCounts[pid] === "number"
              ? commentCounts[pid]
              : p.commentsCount || 0
          );

          // shares number from backend map (fallback to post field)
          const sharesNum = Number(
            shareCounts && typeof shareCounts[pid] === "number"
              ? shareCounts[pid]
              : p.sharesCount || 0
          );

          const rawText = p.text || "";
          const MAX_LEN = 260;
          const isExpanded = !!expandedPosts[pid];
          const isLong = rawText.length > MAX_LEN;
          const visibleText =
            !isLong || isExpanded
              ? rawText
              : rawText.slice(0, MAX_LEN).trimEnd();

          if (USE_POSTCARD) {
            return (
              <div key={pid} data-post-id={pid}>
                <PostCard
                  item={p}
                  isSaved={isSaved}
                  isLiked={isLiked}
                  likesCount={Number(likesCount?.[pid] || 0)}
                  onOpenActions={() => openPostActions(p)}
                  navigate={navigate}
                  timeAgo={timeAgo}
                  ownerView={String(userId || "") === String(p?.userId || p?.authorId || p?.ownerId || "")}
                  onSyncCounts={handleSyncCounts}
                  allowComments={commentsEnabled}
                />
              </div>
            );
          }

          return (
            <div
              key={pid}
              className={`feed__card ${p.photo ? "" : "text-only"}`}
              data-post-id={pid}
            >
              <div className="feed__header">
                <img
                  className="feed__avatar"
                  src={p.authorAvatar || DEFAULT_AVATAR}
                  alt=""
                  onClick={() => navigate(`/profile/${p.userId}`)}
                />
                <div className="feed__author-info">
                  <h4
                    className="feed__author"
                    onClick={() => navigate(`/profile/${p.userId}`)}
                  >
                    {p.authorName || "Користувач"}
                  </h4>
                  <p className="feed__location">{timeAgo}</p>
                </div>

                <div
                  className="feed__more"
                  role="button"
                  title="Дії"
                  onClick={() => openPostActions(p)}
                >
                  ⋮
                </div>
              </div>

              <div className="feed__info">
                {rawText ? (
                  <p className="feed__text">
                    {visibleText}
                    {isLong && !isExpanded && "…"}
                    {isLong && (
                      <button
                        type="button"
                        className="feed__text-toggle"
                        onClick={() =>
                          setExpandedPosts((prev) => ({
                            ...prev,
                            [pid]: !isExpanded,
                          }))
                        }
                      >
                        {isExpanded ? "Згорнути" : "Ще"}
                      </button>
                    )}
                  </p>
                ) : null}
              </div>

              {p.photo ? (
                <div
                  className="feed__image-wrapper"
                  onDoubleClick={() => handleDoubleClick(p)}
                >
                  <img
                    className="feed__image"
                    src={p.photo}
                    alt=""
                    onClick={() => {
                      if (clickTimerRef.current)
                        clearTimeout(clickTimerRef.current);
                      clickTimerRef.current = setTimeout(() => {
                        openPhotoViewer(p, 0);
                        clickTimerRef.current = null;
                      }, 180);
                    }}
                    draggable={false}
                  />

                  <ActionBar
                    variant="inline"
                    liked={isLiked}
                    saved={isSaved}
                    likeCount={Number(likesCount?.[pid] || 0)}
                    commentCount={Number(commentsNum || 0)}
                    shareCount={Number(sharesNum || 0)}
                    savedCount={isSaved ? 1 : 0}
                    onLike={(e) => {
                      e?.stopPropagation?.();
                      toggleLike(p);
                    }}
                    onComment={
                      commentsEnabled
                        ? (e) => {
                            e?.stopPropagation?.();
                            openCommentsForPost(p);
                          }
                        : null
                    }
                    onShare={(e) => {
                      e?.stopPropagation?.();
                      const url = buildCommunityPostUrl(pid);
                      setShareUrl(url);
                      setShareText(p.text ? p.text.slice(0, 120) : "");
                      setSharePostId(pid);
                      setShareOpen(true);
                    }}
                    onSave={async (e) => {
                      e?.stopPropagation?.();
                      if (!userId) {
                        navigate("/auth");
                        return;
                      }
                      await quickSaveToAllIfNeeded(p);
                      setSaveSheetPostId(pid);
                      setSaveSheetThumb(p.photo || null);
                      setSaveSheetOpen(true);
                    }}
                    showComments={commentsEnabled}
                  />
                </div>
              ) : (
                <div className="community-actionbar-floating">
                  <ActionBar
                    variant="floating"
                    liked={isLiked}
                    saved={isSaved}
                    likeCount={Number(likesCount?.[pid] || 0)}
                    commentCount={Number(commentsNum || 0)}
                    shareCount={Number(sharesNum || 0)}
                    savedCount={isSaved ? 1 : 0}
                    onLike={(e) => {
                      e?.stopPropagation?.();
                      toggleLike(p);
                    }}
                    onComment={
                      commentsEnabled
                        ? (e) => {
                            e?.stopPropagation?.();
                            openCommentsForPost(p);
                          }
                        : null
                    }
                    onShare={(e) => {
                      e?.stopPropagation?.();
                      const url = buildCommunityPostUrl(pid);
                      setShareUrl(url);
                      setShareText(p.text ? p.text.slice(0, 120) : "");
                      setSharePostId(pid);
                      setShareOpen(true);
                    }}
                    onSave={async (e) => {
                      e?.stopPropagation?.();
                      if (!userId) {
                        navigate("/auth");
                        return;
                      }
                      await quickSaveToAllIfNeeded(p);
                      setSaveSheetPostId(pid);
                      setSaveSheetThumb(p.photo || null);
                      setSaveSheetOpen(true);
                    }}
                    showComments={commentsEnabled}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* sentinel для infinite scroll */}
        {!feedLoading && posts.length > 0 && hasMore && (
          <div
            ref={loadMoreRef}
            className="community-feed__sentinel"
            aria-hidden="true"
          />
        )}

        {loadingMore && (
          <div className="feed__loading-more">
            Завантаження ще...
          </div>
        )}
      </div>

      {/* modals */}
      {!USE_POSTCARD && (
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            setSharePostId(null);
          }}
          shareUrl={shareUrl}
          shareText={shareText}
          onShared={(recipients) => {
            if (!sharePostId || !Array.isArray(recipients) || !recipients.length)
              return;

            const inc = recipients.length;

            setPosts((prev) =>
              prev.map((post) =>
                post.postId === sharePostId
                  ? {
                      ...post,
                      sharesCount: (post.sharesCount || 0) + inc,
                    }
                  : post
              )
            );

            setShareCounts((prev) => ({
              ...prev,
              [sharePostId]: Math.max(0, Number(prev?.[sharePostId] || 0) + inc),
            }));
          }}
        />
      )}

      {!USE_POSTCARD && (
        <CommentsModal
          open={commentsOpen}
          onClose={closeComments}
          listingId={commentsListingId}
          listingAuthorId={commentsListingAuthorId}
          onCountChange={onCommentsCountChange}
        />
      )}

      {!USE_POSTCARD && (
        <SaveSheetModal
          open={saveSheetOpen}
          onClose={() => setSaveSheetOpen(false)}
          userId={userId}
          postId={saveSheetPostId}
          type="post"
          thumbnail={saveSheetThumb}
          title="Збереження"
          onChanged={({ saved }) => {
            if (!saveSheetPostId) return;
            setSavedIds((prev) =>
              saved
                ? prev.includes(saveSheetPostId)
                  ? prev
                  : [...prev, saveSheetPostId]
                : prev.filter((x) => x !== saveSheetPostId)
            );
          }}
        />
      )}

      {/* post actions (⋮) */}
      {postActionsOpen && postActionsItem && (
        <FeedActions
          type="post"
          id={postActionsItem.postId}
          createdAt={postActionsItem.createdAt}
          isOwner={String(userId || "") === String(postActionsItem.userId || "")}
          onClose={closePostActions}
          onDelete={handlePostDeleted}
          authorId={postActionsItem.userId}
          authorName={postActionsItem.authorName}
          postText={postActionsItem.text}
          postUrl={buildCommunityPostUrl(postActionsItem.postId)}
        />
      )}
    </div>
  );
};

export default CommunityFeed;